import { useRef, useState } from 'react';
import { Search, RefreshCw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@client/lib';
import { useInviteClientTrigger } from '@client/features/invite-client';

interface SearchHeaderProps {
  searchTerm: string;
  onSearchChange: (searchText: string) => void;
  handleRefresh: () => void;
}

export function SearchHeader({ searchTerm, onSearchChange, handleRefresh }: SearchHeaderProps) {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { setInviteClientParams } = useInviteClientTrigger();

  const activateSearch = () => {
    setIsSearchActive(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const deactivateSearch = () => {
    if (!searchTerm) {
      setIsSearchActive(false);
    }
  };

  return (
    <div className="sticky top-0 z-50 border-b border-[#e8e0ff] bg-white">
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <div className="flex flex-1 items-center gap-2 overflow-hidden">
          <div
            className={cn(
              'relative flex items-center transition-all duration-200',
              isSearchActive ? 'flex-1' : 'w-9',
            )}
          >
            {!isSearchActive ? (
              <Button
                variant="outline"
                size="icon"
                onClick={activateSearch}
                className="h-9 w-9 border-[#e8e0ff] text-[#7755CC]"
              >
                <Search className="h-4 w-4" />
              </Button>
            ) : (
              <>
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-700" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search clients…"
                  value={searchTerm}
                  onChange={e => onSearchChange(e.target.value)}
                  onBlur={deactivateSearch}
                  className="w-full rounded-lg border border-[#e8e0ff] bg-white py-2 pl-10 pr-4 text-sm text-gray-950 focus:outline-none"
                />
              </>
            )}
          </div>

          {!isSearchActive && (
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              className="h-9 w-9 border-[#e8e0ff] text-[#7755CC]"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>

        <Button
          className={cn(
            'bg-primary hover:bg-primary-hover flex items-center gap-2 rounded-full text-sm text-white transition-all duration-200',
            isSearchActive ? 'px-3 py-2' : 'px-4 py-2',
          )}
          onClick={() => setInviteClientParams()}
        >
          <Send className="h-4 w-4" />
          {!isSearchActive && <span>Add Clients</span>}
        </Button>
      </div>
    </div>
  );
}
